import { AlertTriangle, ArrowRight, CheckCircle, ClipboardCheck, FileText, PoundSterling, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/waitlist/Footer';
import { Navbar } from '../components/waitlist/Navbar';
import { getFleetTrialSignupUrl } from '../config/urls';
import { usePageMeta } from '../hooks/usePageMeta';
import { Analytics } from '../services/analytics';

export function PcoHireWorkflow() {
  usePageMeta({
    title: 'PCO Hire PCN Management Software - PCNGuard FleetMode',
    description: 'AI-first PCO hire PCN management for driver matching, transfer of liability readiness, evidence gaps, deadline risk, and admin-fee recovery tracking.',
    canonical: 'https://pcnguard.co.uk/pco-hire-pcn-management',
    ogTitle: 'PCO hire PCN management software',
    ogDescription: 'AI-first PCN control for PCO hire companies handling driver matching, evidence control, liability readiness, and recovery status.',
  });

  const trialUrl = getFleetTrialSignupUrl();

  const trackClick = (placement: string) => {
    Analytics.track('pco_hire_workflow_cta_clicked', {
      placement,
      destination: trialUrl,
      audience: 'pco_hire',
    });
  };

  const workflow = [
    {
      title: 'Parking charge notice driver matching',
      detail: 'Connect the PCN to the right vehicle, driver, and hire period before action.',
      icon: Users,
    },
    {
      title: 'Transfer of liability software support',
      detail: 'Prepare reviewed PCN details, driver information, hire records, and supporting notes for liability workflows.',
      icon: FileText,
    },
    {
      title: 'PCN admin fee recovery visibility',
      detail: 'Track recovered, pending, waived, disputed, and written-off PCN costs and admin fees.',
      icon: PoundSterling,
    },
    {
      title: 'Evidence and deadline risk checks',
      detail: 'Spot missing hire evidence and urgent discount or representation deadlines before they are missed.',
      icon: AlertTriangle,
    },
  ] as const;

  return (
    <div className="min-h-screen bg-[#fffaf1] text-[#081b1f] selection:bg-[#d9a45b]/25 font-sans">
      <Navbar />

      <main id="main-content">
        <section className="border-b border-[#d7c3a8] bg-[#f4eadb] px-4 pb-14 pt-24 sm:px-6 sm:pb-18 sm:pt-28">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                PCO hire PCN management
              </div>
              <h1 className="mt-5 font-space text-4xl font-black leading-tight tracking-tight text-[#081b1f] sm:text-5xl">
                AI-first fleet parking fine management for UK PCO hire companies.
              </h1>
              <p className="mt-5 text-base leading-7 text-[#596764] sm:text-lg">
                PCNGuard FleetMode helps PCO hire operators process parking charge notices, match drivers, check liability readiness, control evidence gaps, and track admin-fee recovery from one workflow.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={trialUrl}
                  onClick={() => trackClick('hero')}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#194d3a] px-6 py-3 font-bold text-white shadow-lg shadow-[#194d3a]/20 transition-all duration-200 hover:bg-[#26654e] active:translate-y-px"
                >
                  Check 3 PCNs free
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/fleet"
                  className="inline-flex items-center justify-center rounded-md border border-[#d6c7b6] bg-white px-6 py-3 font-bold text-[#344347] transition-all duration-200 hover:border-[#194d3a] hover:text-[#194d3a]"
                >
                  View FleetMode
                </Link>
                <Link
                  to="/fleet/features"
                  className="inline-flex items-center justify-center rounded-md border border-[#d6c7b6] bg-white px-6 py-3 font-bold text-[#344347] transition-all duration-200 hover:border-[#194d3a] hover:text-[#194d3a]"
                >
                  Explore features
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-2 xl:grid-cols-4">
            {workflow.map((item) => (
              <article key={item.title} className="rounded-lg border border-[#e4d8c8] bg-[#f6f0e6] p-5 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]">
                  <item.icon className="h-5 w-5" />
                </div>
                <h2 className="mt-4 font-space text-lg font-bold">{item.title}</h2>
                <p className="mt-3 text-sm leading-6 text-[#596764]">{item.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-[#081b1f] px-4 py-14 text-white sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <div className="inline-flex rounded-md border border-white/15 px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#d9a45b]">
                Why it matters
              </div>
              <h2 className="mt-5 font-space text-3xl font-bold leading-tight sm:text-4xl">
                Every PCN needs a clear action trail.
              </h2>
              <p className="mt-5 text-base leading-7 text-[#c8d2cf]">
                PCO hire teams need to know who had the vehicle, whether transfer evidence is ready, which deadline is closest, and whether the PCN cost or admin fee has been recovered.
              </p>
            </div>

            <div className="grid gap-3">
              {[
                'Upload a PCN and review AI-extracted fields.',
                'Match notice details to driver and hire-period context.',
                'Flag missing hire evidence before transfer or follow-up.',
                'Choose transfer, appeal, pay, recharge, or further review as the next action.',
              ].map((point) => (
                <div key={point} className="flex gap-3 border border-white/10 bg-white/[0.06] p-4">
                  <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#d9a45b]" />
                  <p className="text-sm leading-6 text-[#c8d2cf]">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#eaf3ee] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-5xl rounded-lg border border-[#b5d5c4] bg-[#fffaf1] p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#194d3a]">
                  <ClipboardCheck className="h-4 w-4" />
                  Product-led proof
                </div>
                <h2 className="mt-3 font-space text-3xl font-bold">Start with three real PCNs.</h2>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-[#596764]">
                  Use a small sample to see extraction quality, evidence gaps, deadline risk, and recovery status before moving more of your PCO hire workflow into FleetMode.
                </p>
              </div>
              <a
                href={trialUrl}
                onClick={() => trackClick('final')}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-[#194d3a] px-6 py-3 font-bold text-white shadow-lg shadow-[#194d3a]/20 transition-all duration-200 hover:bg-[#26654e] active:translate-y-px"
              >
                Check 3 PCNs free
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default PcoHireWorkflow;
